import { useState, useCallback } from "react";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";

import VerticalRegistry from "../../../constants/verticalRegistry";
import { useSearchUser } from "../../../hooks/useSearchUser";
import { useSearchBranches } from "../../../hooks/useSearchBranches";
import { searchSchema, validateSearchForm } from "../validation/searchSchema";
import { filterMatchedProfiles } from "../utils/studentSearchHelpers";
import { useSearchUIState } from "./useSearchUIState";
import { CONSTANTS, MIN_SEARCH_LENGTH } from "../../../constants/constants";

export const useStudentSearch = () => {
  const { mutateAsync } = useSearchUser();
  const { mutateAsync: searchBranches } = useSearchBranches();

  const [selectedBranch, setSelectedBranch] = useState(null);

  const {
    errorSnackbar,
    openErrorSnackbar,
    closeErrorSnackbar,
    profilesModal,
    openProfilesModal,
    closeProfilesModal,
    handleProfileSelect,
  } = useSearchUIState();

  const selectProfile = useCallback(
    (profile, searchValues) =>
      handleProfileSelect(profile, searchValues, selectedBranch),
    [handleProfileSelect, selectedBranch],
  );

  const formik = useFormik({
    initialValues: {
      vertical: "IL",
      admission_number: "",
      phone: "",
      uam_id: "",
      branch_name: "",
      branch_id: "",
    },
    validationSchema: toFormikValidationSchema(searchSchema),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: validateSearchForm,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const response = await mutateAsync({
          vertical: values.vertical,
          searchParams: values,
        });

        const verticalObj = VerticalRegistry.getVerticalByName(values.vertical);
        const finalProfiles = filterMatchedProfiles(
          response,
          values,
          verticalObj,
          selectedBranch,
        );

        if (finalProfiles.length === CONSTANTS.ONE) {
          selectProfile(finalProfiles[0], values);
        } else if (finalProfiles.length > CONSTANTS.ONE) {
          openProfilesModal(finalProfiles, values);
        } else {
          openErrorSnackbar(
            "No user found with the provided details for the selected vertical.",
          );
        }
      } catch {
        openErrorSnackbar("No user found. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const fetchSchoolBranches = useCallback(
    async (searchText) => {
      try {
        if (searchText.length < MIN_SEARCH_LENGTH) return [];
        return await searchBranches(searchText);
      } catch (error) {
        console.error("Failed to fetch branches:", error);
        return [];
      }
    },
    [searchBranches],
  );

  return {
    formik,
    selectedBranch,
    setSelectedBranch,
    fetchSchoolBranches,
    errorSnackbar,
    closeErrorSnackbar,
    profilesModal,
    closeProfilesModal,
    handleProfileSelect: selectProfile,
  };
};
